// dashboard/widgets/MiniWidgets.js
// The three small tracker-preview lines shown on a PetCard (weight/
// wellbeing/seizures) — always used together, extracted from App.js
// (2026-08-12 split).

import { useTranslation } from 'react-i18next';
import { c, onActivateKeyDown } from '../constants';
import { fmtWeight, relDate } from '../utils';

export function WeightMiniWidget({ weights, onClick, useLbs }) {
  const { t } = useTranslation();
  if (!weights?.length) return null;
  const latest = weights[0]; const oldest = weights[weights.length - 1];
  const pct = weights.length >= 2 ? ((latest.weight - oldest.weight) / oldest.weight) * 100 : 0;
  const arrow = Math.abs(pct) <= 5 ? '→' : pct > 0 ? '↗' : '↘';
  const label = Math.abs(pct) <= 5 ? t('trackers.weight_trend_stable') : pct > 0 ? t('trackers.weight_trend_gaining') : t('trackers.weight_trend_losing');
  const col   = pct < -5 ? c.red : pct > 5 ? c.yellow : c.green;
  // weights arrives newest-first (see fetchTrackerData's query order) — the
  // trend/pct math above correctly uses that order, but reversed here so the
  // arrow chain reads left-to-right as chronological (oldest → newest),
  // matching how "→" naturally reads. Un-reversed, a gaining pet's numbers
  // read as if they were falling, and vice versa.
  const recent = weights.slice(0, 3).reverse().map(w => fmtWeight(w.weight, useLbs)).join(' → ');
  return (
    <div
      style={{ marginBottom: 6, cursor: 'pointer' }}
      onClick={onClick}
      onKeyDown={onActivateKeyDown(onClick)}
      tabIndex={0}
      role="button"
      aria-label={t('patientCard.weightTrendAria', { label, recent })}
    >
      <span style={{ fontSize: 11, fontWeight: 600, color: c.textSoft, textTransform: 'uppercase', letterSpacing: 0.5 }}>{t('patientCard.weightLabel')} </span>
      <span style={{ fontSize: 13, color: col }}>{arrow} {label}</span>
      <span style={{ fontSize: 11, color: c.textSoft }}> · {recent}</span>
    </div>
  );
}

export function WellbeingMiniWidget({ goodDays, onClick }) {
  const { t } = useTranslation();
  if (!goodDays?.length) return null;
  const good = goodDays.filter(d => d.value === 'good').length;
  const diff = goodDays.filter(d => d.value === 'difficult').length;
  return (
    <div
      style={{ marginBottom: 6, cursor: 'pointer' }}
      onClick={onClick}
      onKeyDown={onActivateKeyDown(onClick)}
      tabIndex={0}
      role="button"
      aria-label={diff > 0
        ? t('patientCard.wellbeingAriaWithDifficult', { good, difficult: diff })
        : t('patientCard.wellbeingAria', { good })}
    >
      <span style={{ fontSize: 11, fontWeight: 600, color: c.textSoft, textTransform: 'uppercase', letterSpacing: 0.5 }}>{t('patientCard.wellbeingLabel')} </span>
      <span style={{ fontSize: 12, color: c.green }}>{t('patientCard.goodCount', { count: good })}</span>
      {diff > 0 && <span style={{ fontSize: 12, color: c.yellowText }}> · {t('patientCard.difficultCount', { count: diff })}</span>}
      <span style={{ fontSize: 11, color: c.textSoft }}> {t('patientCard.last90d')}</span>
    </div>
  );
}

export function SeizureMiniWidget({ seizures, onClick }) {
  const { t } = useTranslation();
  if (!seizures?.length) return null;
  let summary;
  if (seizures.length >= 2) {
    const sorted = [...seizures].sort((a, b) => new Date(a.occurred_at) - new Date(b.occurred_at));
    const intervals = sorted.slice(1).map((s, i) =>
      (new Date(s.occurred_at) - new Date(sorted[i].occurred_at)) / 86400000
    );
    const avg = Math.round(intervals.reduce((a, b) => a + b, 0) / intervals.length);
    summary = t('patientCard.avgBetween', { count: avg });
  } else {
    summary = t('patientCard.oneInSixMonths');
  }
  return (
    <div
      style={{ marginBottom: 6, cursor: 'pointer' }}
      onClick={onClick}
      onKeyDown={onActivateKeyDown(onClick)}
      tabIndex={0}
      role="button"
      aria-label={t('patientCard.seizuresAria', { summary, date: relDate(seizures[0]?.occurred_at) })}
    >
      <span style={{ fontSize: 11, fontWeight: 600, color: c.textSoft, textTransform: 'uppercase', letterSpacing: 0.5 }}>{t('patientCard.seizuresLabel')} </span>
      <span style={{ fontSize: 12, color: c.yellowText }}>{summary}</span>
      <span style={{ fontSize: 11, color: c.textSoft }}> · {t('patientCard.lastDate', { date: relDate(seizures[0]?.occurred_at) })}</span>
    </div>
  );
}
